// import packages
import React, { useContext, useEffect, useMemo, useState } from "react";
import {
  Button,
  Checkbox,
  DisplayText,
  List,
  Modal,
  OptionList,
  Popover,
  Select,
  Stack,
  TextField,
} from "@shopify/polaris";
import { useMutation } from "@apollo/react-hooks";

// import components
import Banner from "lib/components/banner/banner";
import AutoComplete from "lib/components/autocomplete/autocomplete";

// import helpers
import { baseHelper, errorHelper } from "lib/helpers";

// import context
import { PrivateContext } from "lib/context";

// import constants
import constant from "lib/constant/constant";

// import gql
import { ADD_RULE } from "app/shipping/apollo/mutations";

// import propType

import { addRuleProp } from "app/shipping/modules/operator/features/rule/propTypes";

const AddRule = (props) => {
  const {
    shippingRule: shippingRuleProp = {},
    onClose,
    destinations,
    show,
    bands,
    products,
    handleBanner,
    refetch,
  } = props;
  const { currentUser, cms } = useContext(PrivateContext);

  const defaultRule = {
    condition: "",
    description: "",
    destination: {},
    isShippingDisabled: false,
    price: 0,
    priceType: "",
    productIds: [],
    shippingBandId: "",
  };
  const defaultBanner = {
    isOpen: false,
    status: "",
    title: "",
  };
  const [shippingRule, setShippingRule] = useState({ ...defaultRule });
  const [banner, setBanner] = useState({ ...defaultBanner });
  const [popoverActive, setPopoverActive] = useState(false);
  const [statePopoverActive, setStatePopoverActive] = useState(null);
  const [regionSearch, setRegionSearch] = useState("");
  const [stateSearch, setStateSearch] = useState("");

  const [addRule, { loading: addRuleLoading }] = useMutation(ADD_RULE);

  const normalizedDestinations = useMemo(() => {
    if (!destinations || !Array.isArray(destinations)) return [];
    return destinations;
  }, [destinations]);

  const filteredDestinations = useMemo(() => {
    if (!regionSearch) return normalizedDestinations;
    const search = regionSearch.toLowerCase().trim();
    return normalizedDestinations
      .map((section) => {
        const options =
          (section.options || []).filter((opt) => {
            return (
              (opt.label && opt.label.toLowerCase().includes(search)) ||
              (opt.value && String(opt.value).toLowerCase().includes(search)) ||
              (section.title && section.title.toLowerCase().includes(search))
            );
          }) || [];
        return { ...section, options };
      })
      .filter((section) => section.options && section.options.length);
  }, [normalizedDestinations, regionSearch]);

  useEffect(() => {
    if (Object.keys(shippingRuleProp).length !== 0 && shippingRuleProp !== shippingRule) {
      setShippingRule(shippingRuleProp);
    }
  }, [shippingRule, shippingRuleProp]);

  const checkValidation = () => {
    const { shippingBandId, price, description, condition, destination = {}, productIds, priceType } =
      shippingRule || {};
    if (!shippingBandId) {
      return cms("message.error.selectShippingBand");
    }
    if (!condition) {
      return cms("message.error.selectShippingCondition");
    }
    if (condition === constant.DESTINATION && !Object.keys(destination).length) {
      return cms("message.error.selectAtleastOneDestination");
    }
    if (condition === constant.PRODUCT && !(productIds && productIds.length)) {
      return cms("message.error.selectAtleastOneProduct");
    }
    if (!description) {
      return cms("message.error.addDescriptionForRule");
    }
    if (description.length > 255) {
      return cms("message.error.descriptionCharacter");
    }
    if (!priceType) {
      return cms("message.error.selectShippingRuleType");
    }
    if (priceType !== constant.FREE && !price) {
      return cms("message.error.provideCost");
    }
    if (priceType !== constant.FREE && price < 0) {
      return cms("message.error.provideValidPrice");
    }
    return false;
  };

  const closeModal = () => {
    setBanner({ ...defaultBanner });
    setStatePopoverActive(null);
    onClose();
  };

  const addShippingBand = async () => {
    const requestData = {
      ...shippingRule,
      destination: shippingRule.destination || {},
    };
    try {
      const response = await addRule({
        variables: { input: requestData },
      });
      const responseError = baseHelper.getResponseError(response.data, constant.gql.CREATE_SHIPPING_RULE);
      if (responseError) {
        setBanner({ isOpen: true, status: "critical", title: responseError });
        return;
      }
      setShippingRule({ ...defaultRule });
      setStatePopoverActive(null);
      handleBanner({ isOpen: true, status: "success", title: cms("modal.rule.add.message.success.successfullyAdded") });
      setPopoverActive(false);
      closeModal();
      refetch();
    } catch (exception) {
      setBanner({ isOpen: true, status: "critical", title: errorHelper.parse(exception) });
    }
  };

  const onSubmit = () => {
    const hasError = checkValidation();
    if (hasError) {
      setBanner({ isOpen: true, status: "critical", title: hasError });
      return false;
    }
    addShippingBand();
    return true;
  };

  const toggleBanner = () => setBanner({ ...defaultBanner });

  const handleShippingRule = (key, value) => {
    
    if (key === constant.DESTINATION_CODE) {
      const newDestination = {};
      (value || []).forEach((countryCode) => {
        newDestination[countryCode] = (shippingRule.destination || {})[countryCode] ?? [];
      });
      setShippingRule({ ...shippingRule, destination: newDestination });
      return;
    }
    setShippingRule({ ...shippingRule, [key]: value });
  };

  const handleStateSelection = (countryCode, stateValues) => {
    setShippingRule((prev) => ({
      ...prev,
      destination: {
        ...(prev.destination || {}),
        [countryCode]: stateValues || [],
      },
    }));
  };

  const togglePopover = () => {
    if (popoverActive) {
      setStatePopoverActive(null);
      setStateSearch("");
      setRegionSearch("");
    }
    setPopoverActive(!popoverActive);
  };

  const getSelectedCountryWithStates = () => {
    const { destination = {} } = shippingRule || {};
    const result = [];
    normalizedDestinations.forEach((region) => {
      (region.options || []).forEach((country) => {
        if (destination.hasOwnProperty(country.value)) {
          const states = country.states || [];
          result.push({
            ...country,
            regionTitle: region.title,
            states,
          });
        }
      });
    });
    return result;
  };

  const renderDestinations = () => {
    const { destination = {} } = shippingRule || {};
    const selectedCountries = getSelectedCountryWithStates();

    if (!selectedCountries.length) {
      return null;
    }

    return (
      <Stack vertical>
        <Stack.Item>
          <DisplayText size={constant.SMALL}>{cms("modal.rule.add.label.selected")}</DisplayText>
        </Stack.Item>
        {selectedCountries.map((country) => {
          const selectedStateCodes = destination[country.value] || [];
          const filteredStates =
            (country.states || []).filter((s) => {
              if (!stateSearch) return true;
              const search = stateSearch.toLowerCase().trim();
              return (
                (s.label && s.label.toLowerCase().includes(search)) ||
                (s.value && String(s.value).toLowerCase().includes(search))
              );
            }) || [];
          return (
            <Stack.Item key={country.value}>
              <Stack vertical>
                <Stack.Item>
                  <Stack>
                    <Stack.Item fill>
                      <DisplayText size={constant.SMALL}>
                        <strong>{country.label}</strong>
                      </DisplayText>
                    </Stack.Item>
                    {country.states && country.states.length > 0 && (
                      <Stack.Item>
                        <Popover
                          active={statePopoverActive === country.value}
                          activator={
                            <Button
                              size="slim"
                              onClick={() => {
                                const isSame = statePopoverActive === country.value;
                                setStatePopoverActive(isSame ? null : country.value);
                                if (!isSame) {
                                  setStateSearch("");
                                }
                              }}
                            >
                              {cms("modal.rule.add.button.selectStates") || "Select states"}
                            </Button>
                          }
                          onClose={() => {
                            setStatePopoverActive(null);
                            setStateSearch("");
                          }}
                        >
                          <div style={{ padding: "12px", minWidth: "260px", maxHeight: "300px", overflowY: "auto" }}>
                            <Stack vertical spacing="tight">
                              <Stack.Item>
                                <TextField
                                  value={stateSearch}
                                  onChange={setStateSearch}
                                  placeholder={
                                    cms("modal.rule.add.placeholder.searchState") || "Search state or province"
                                  }
                                />
                              </Stack.Item>
                              <Stack.Item>
                                <OptionList
                                  onChange={(val) => handleStateSelection(country.value, val)}
                                  sections={[
                                    {
                                      options: filteredStates.map((s) => ({
                                        value: s.value,
                                        label: s.label,
                                      })),
                                    },
                                  ]}
                                  selected={selectedStateCodes}
                                  allowMultiple
                                />
                              </Stack.Item>
                            </Stack>
                          </div>
                        </Popover>
                      </Stack.Item>
                    )}
                  </Stack>
                </Stack.Item>
                {selectedStateCodes.length > 0 ? (
                  <Stack.Item>
                    <List type={constant.BULLET}>
                      {selectedStateCodes.map((stateCode) => {
                        const state = (country.states || []).find((s) => s.value === stateCode);
                        return state ? <List.Item key={stateCode}>{state.label}</List.Item> : null;
                      })}
                    </List>
                  </Stack.Item>
                ) : (
                  country.states &&
                  country.states.length > 0 && (
                    <Stack.Item>
                      <DisplayText size={constant.SMALL} tone="subdued">
                        All states
                      </DisplayText>
                    </Stack.Item>
                  )
                )}
              </Stack>
            </Stack.Item>
          );
        })}
      </Stack>
    );
  };

  const {
    shippingBandId,
    price = 0,
    description,
    condition,
    destination = {},
    productIds = [],
    priceType,
    isShippingDisabled,
  } = shippingRule || {};
  shippingRule.price = parseFloat(price);
  const { moneyFormat = constant.symbol.DOLLAR } = currentUser || {};

  const isFieldDisabled = false;

  const isDestination = condition === constant.DESTINATION;
  const isProduct = condition === constant.PRODUCT;

  const activator = <Button onClick={togglePopover}>{cms("modal.rule.add.button.selectRegion")}</Button>;
  const priceTypeOptions = [
    {
      label: cms("label.fixed"),
      value: constant.FIXED,
    },
    {
      label: cms("modal.rule.add.label.free"),
      value: constant.FREE,
    },
  ];
  const conditionOptions = [
    {
      label: cms("modal.rule.add.label.destination"),
      value: constant.DESTINATION,
    },
    {
      label: cms("modal.rule.add.label.product"),
      value: constant.PRODUCT,
    },
  ];

  const acceptOnlyValidInput = (value, prevValue) => {
    return (baseHelper.validatePositiveNumericValues(value) && value) || (value !== "" && prevValue) || "";
  };

  return (
    <Modal
      open={show}
      onClose={closeModal}
      title={cms("modal.rule.add.title")}
      primaryAction={{
        content: cms("modal.rule.add.button.primary"),
        loading: addRuleLoading,
        onAction: () => onSubmit(),
      }}
    >
      <Modal.Section>
        {banner.isOpen && (
          <>
            <Banner onDismiss={toggleBanner} isOpen={banner.isOpen} status={banner.status} title={banner.title} />
            <br />
          </>
        )}
        <Stack vertical>
          <Stack.Item>
            <Select
              options={bands}
              name={constant.SHIPPING_BAND}
              label={cms("modal.rule.add.label.selectShippingBand")}
              placeholder={cms("modal.rule.add.label.selectShippingBand")}
              value={shippingBandId || ""}
              onChange={(val) => handleShippingRule(constant.SHIPPING_BAND_ID, val)}
            />
          </Stack.Item>
          <Stack.Item>
            <Select
              options={conditionOptions}
              name={constant.SHIPPING_CONDITION}
              label={cms("modal.rule.add.label.selectShippingRule")}
              placeholder={cms("modal.rule.add.label.selectShippingRule")}
              value={condition || ""}
              onChange={(val) => handleShippingRule(constant.CONDITION, val)}
            />
          </Stack.Item>
          {isDestination && (
            <>
              <Stack.Item>
                <Popover active={popoverActive} activator={activator} onClose={togglePopover}>
                  <div style={{ padding: "12px", minWidth: "320px", maxHeight: "400px", overflowY: "auto" }}>
                    <Stack vertical spacing="tight">
                      <Stack.Item>
                        <TextField
                          value={regionSearch}
                          onChange={setRegionSearch}
                          placeholder={cms("modal.rule.add.placeholder.searchRegion") || "Search region or country"}
                        />
                      </Stack.Item>
                      <Stack.Item>
                        <OptionList
                          onChange={(val) => handleShippingRule(constant.DESTINATION_CODE, val)}
                          sections={filteredDestinations}
                          selected={Object.keys(destination)}
                          allowMultiple
                        />
                      </Stack.Item>
                    </Stack>
                  </div>
                </Popover>
              </Stack.Item>
              <Stack.Item>{renderDestinations()}</Stack.Item>
            </>
          )}
          {isProduct && (
            <Stack.Item>
              <AutoComplete
                id={constant.PRODUCT_RULE_CONDITION}
                label={cms("modal.rule.add.label.products")}
                labelHidden
                placeholder={cms("modal.rule.add.placeholder.startTyping")}
                values={products}
                selected={productIds}
                onChange={(val) => handleShippingRule(constant.PRODUCT_IDS, val)}
                minimumSearchLength={constant.value.MIN_SEARCH_LENGTH}
              />
            </Stack.Item>
          )}
          <Stack.Item>
            <TextField
              id={constant.INPUT_ID_DESCRIPTION}
              type={constant.TEXT}
              label={`${cms("modal.rule.add.label.description")}*`}
              placeholder={cms("modal.rule.add.placeholder.description")}
              value={description}
              onChange={(val) => handleShippingRule(constant.DESCRIPTION, val)}
            />
          </Stack.Item>
          <Stack.Item>
            <Stack>
              <Stack.Item>
                <Select
                  options={priceTypeOptions}
                  id={constant.INPUT_ID_TYPE}
                  name={constant.SHIPPING_TYPE}
                  label={cms("modal.rule.add.label.selectShippingType")}
                  placeholder={cms("modal.rule.add.placeholder.selectShipping")}
                  value={priceType || ""}
                  disabled={isFieldDisabled}
                  onChange={(val) => handleShippingRule(constant.PRICE_TYPE, val)}
                />
              </Stack.Item>
              {priceType === constant.FIXED && (
                <Stack.Item>
                  <TextField
                    id={constant.INPUT_ID_COST}
                    label={cms("modal.rule.add.label.amount")}
                    min={constant.value.MIN_PRICE}
                    step={constant.value.STEP}
                    value={price.toString() || ""}
                    disabled={isFieldDisabled}
                    onChange={(val) => handleShippingRule(constant.PRICE, acceptOnlyValidInput(val, price))}
                    prefix={moneyFormat}
                  />
                </Stack.Item>
              )}
            </Stack>
          </Stack.Item>
          <Stack.Item>
            <Checkbox
              checked={isShippingDisabled}
              onChange={() => handleShippingRule(constant.IS_SHIPPING_DISABLED, !isShippingDisabled)}
              label={cms("modal.rule.add.label.isAvailableForShipping")}
            />
          </Stack.Item>
        </Stack>
      </Modal.Section>
    </Modal>
  );
};

AddRule.propTypes = addRuleProp.type;

export default AddRule;
